// Moldura das telas de entrada (login do cliente, convite, senha, login da equipe Resulti), com a cara
// da tela de entrada do PlugChef Desktop (plugchef.desktop/.../screens/LoginScreen.tsx): fundo em
// gradiente, o botão de tema no canto, a ilustração do chef à esquerda (só em tela larga) e o cartão
// com o logo. A página põe dentro do cartão só o conteúdo — formulário, avisos, resultado —, sem
// outro `card` (seria um cartão dentro do outro); o corpo do formulário usa `corpoEntrada`.
import type { ReactNode } from 'react';
import { useTema } from '../lib/tema';
import { Marca } from './base';
import { IconMoon, IconSun } from './desktop/icones';
import { IlustracaoChef } from './desktop/IlustracaoChef';

/** O corpo de um formulário de entrada: os campos empilhados com o espaço do Desktop. */
export const corpoEntrada = 'flex flex-col gap-4';

export function TelaLogin({
  area, subtitulo, faixa, children, rodape,
}: {
  /** A área, num selo embaixo do logo ("Admin", "Área Resulti"). Sem ela, "Admin". */
  area?: string;
  subtitulo: ReactNode;
  /** Faixa no topo (a Área Resulti se identifica antes mesmo do login). */
  faixa?: ReactNode;
  children: ReactNode;
  /** Texto pequeno no pé do cartão (no Desktop, o "Conectando em…"). */
  rodape?: ReactNode;
}): JSX.Element {
  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-bg via-panel to-bg">
      {faixa}
      {/* `relative` aqui, e não na página: o botão de tema fica abaixo da faixa, nunca por cima dela. */}
      <div className="relative flex flex-1 items-center justify-center">
        <div className="absolute right-3 top-3 z-10 sm:right-4 sm:top-4">
          <BotaoTemaEntrada />
        </div>

        {/* O espaço de cima é o do botão de tema: em 390 px o cartão não passa por baixo dele. */}
        <div className="flex w-full max-w-[1100px] items-center justify-center gap-10 px-4 pb-10 pt-[68px] sm:px-10 lg:justify-between lg:py-16">
          <div className="hidden flex-1 items-center justify-center lg:flex">
            <IlustracaoChef />
          </div>

          <div className="w-full min-w-0 max-w-[440px] rounded-[24px] bg-card p-6 shadow-[0_20px_60px_-15px_rgba(15,40,70,0.25)] sm:p-9">
            <div className="flex flex-col items-center text-center">
              <Marca area={null} className="text-[26px]" />
              <span className="mt-2.5 inline-flex rounded-full bg-accent/10 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-accent">
                {area ?? 'Admin'}
              </span>
              <p className="mt-2 text-[14px] leading-relaxed text-mut">{subtitulo}</p>
            </div>

            <div className="mt-8">{children}</div>

            {rodape ? <div className="mt-6 text-center text-[12px] leading-relaxed text-mut2">{rodape}</div> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/** O botão de tema da tela de entrada do Desktop (sol/lua num quadrado de 44 px). */
function BotaoTemaEntrada(): JSX.Element {
  const [tema, alternar] = useTema();
  const texto = tema === 'dark' ? 'Ativar modo claro' : 'Ativar modo escuro';
  return (
    <button
      type="button"
      title={texto}
      aria-label={texto}
      onClick={alternar}
      className="flex h-11 w-11 items-center justify-center rounded-xl bg-card text-mut transition-colors hover:text-accent"
    >
      {tema === 'dark' ? <IconSun size={20} /> : <IconMoon size={20} />}
    </button>
  );
}

/** Faixa que identifica a Área Resulti (no login e dentro da área). */
export function FaixaResulti(): JSX.Element {
  return (
    // Fundo âmbar sólido com texto escuro: legível nos dois temas (âmbar sobre âmbar claro some no
    // tema claro) e impossível de confundir com a Área do Cliente.
    <div className="bg-amber text-[#1f1300] text-[11px] font-bold tracking-wide uppercase text-center px-3 py-1.5">
      Área Resulti · acesso interno — o que você abre e altera fica registrado na auditoria
    </div>
  );
}

/** "Carregando" dentro do cartão de entrada (o `Carregando` do site é um cartão, e aqui ficaria um dentro do outro). */
export function CarregandoEntrada({ altura = 'h-48' }: { altura?: string }): JSX.Element {
  return <div className={`${altura} rounded-xl bg-inputBg animate-pulseSuave`} aria-busy="true" aria-label="Carregando" />;
}
